import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { safeSessionStorage } from '../utils/storageUtils';

const ViewContext = createContext();

export const useView = () => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error('useView must be used within a ViewProvider');
  }
  return context;
};

export const ViewProvider = ({ children }) => {
  const { profile, isSuperAdmin } = useAuth();
  
  // Initialize view mode state
  const getInitialViewMode = () => {
    return {
      active: false,
      userType: null,
      userId: null,
      userName: null,
      tenantId: null,
      tenantName: null
    };
  };

  // View As state
  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from session storage on mount (only once, after auth is ready)
  useEffect(() => {
    if (isInitialized || !isSuperAdmin) {
      // Only restore for super admins
      if (!isSuperAdmin && isInitialized) {
        // Clear view mode if user is not super admin
        const saved = safeSessionStorage.getItem('viewMode');
        if (saved) {
          safeSessionStorage.removeItem('viewMode');
          setViewMode(getInitialViewMode());
        }
      }
      return;
    }
    
    const saved = safeSessionStorage.getItem('viewMode');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only restore if it's a valid active view mode
        if (parsed && typeof parsed === 'object' && parsed.active && parsed.tenantId) {
          setViewMode(parsed);
          if (import.meta.env.DEV) {
            console.log('View mode restored from sessionStorage:', parsed);
          }
        } else {
          // Invalid or inactive - clear it
          safeSessionStorage.removeItem('viewMode');
        }
      } catch (error) {
        console.error('Failed to parse saved view mode:', error);
        safeSessionStorage.removeItem('viewMode');
      }
    }
    setIsInitialized(true);
  }, [isInitialized, isSuperAdmin]);

  // Save to session storage when changed (only for super admins)
  useEffect(() => {
    if (!isInitialized || !isSuperAdmin) return; // Don't save during initialization or if not super admin
    
    if (viewMode.active) {
      // Save active view mode (with error handling)
      safeSessionStorage.setItem('viewMode', JSON.stringify(viewMode));
    } else {
      // Clear inactive view mode (with error handling)
      safeSessionStorage.removeItem('viewMode');
    }
  }, [viewMode, isInitialized, isSuperAdmin]);

  // Enter view mode - can view as a user or as a clinic
  const enterViewMode = (userType, userId, userName, tenantId, tenantName) => {
    if (!isSuperAdmin) {
      console.warn('Only super admins can use View As mode');
      return;
    }

    setViewMode({
      active: true,
      userType,
      userId,
      userName,
      tenantId,
      tenantName
    });
  };

  // Enter clinic view mode (view as clinic without specific user)
  const enterClinicViewMode = (tenantId, tenantName) => {
    if (!isSuperAdmin) {
      console.warn('Only super admins can use View As mode');
      return;
    }

    const newViewMode = {
      active: true,
      userType: 'clinic', // Special type for clinic-level view
      userId: null, // No specific user
      userName: null,
      tenantId,
      tenantName
    };
    
    // Save to sessionStorage immediately (with error handling)
    safeSessionStorage.setItem('viewMode', JSON.stringify(newViewMode));
    setViewMode(newViewMode);
  };

  // Exit view mode
  const exitViewMode = () => {
    const clearedMode = {
      active: false,
      userType: null,
      userId: null,
      userName: null,
      tenantId: null,
      tenantName: null
    };
    
    // Clear sessionStorage immediately (with error handling)
    safeSessionStorage.removeItem('viewMode');
    setViewMode(clearedMode);
  };

  // Get effective user (either actual user or viewed user)
  const getEffectiveUserId = () => {
    return viewMode.active ? viewMode.userId : profile?.id;
  };

  // Get effective tenant
  const getEffectiveTenantId = () => {
    return viewMode.active ? viewMode.tenantId : profile?.tenant_id;
  };

  const value = {
    viewMode,
    isViewingAs: viewMode.active,
    enterViewMode,
    enterClinicViewMode,
    exitViewMode,
    getEffectiveUserId,
    getEffectiveTenantId
  };

  return (
    <ViewContext.Provider value={value}>
      {children}
    </ViewContext.Provider>
  );
};

export default ViewContext;
